import React from 'react'
import { connect } from 'react-redux'
import { compact, uniq, findIndex, findLastIndex, last, get } from 'lodash'
import moment from 'moment'
import FileSaver from 'file-saver'
import { Modal, Panel, Glyphicon, Checkbox } from 'react-bootstrap'

import {
  calculateTripPriceFromBill, calculateItemPrice,
  getItemFee, getFactorGestion, countAdults, countKids
} from '../../lib/TripTools.js'

import { printMoney } from '../../lib/Tools.js'
import {  getText, getTransportationLabel, transportationIncludesTake } from '../lib/text'
import { createExcelBlob, contratoExcelStyle } from '../../lib/xlsx.js'
import Button from '../components/Button.jsx'
import { getBillForProps } from '../selectors'
import TripNamePicker from './TripNamePicker'

/**
 * WARNING
 * generateCSVData está muy acoplado con getTripBill
 * depende de que la primera sección sean los gastos, la segunda el transporte, y luego las actividades
 * esto puede cambiar (y cambiará, por ejemplo, cuando lleguen los hoteles
 *
 * solución: identificar cada section con un type o algo y usar eso
 *
 * TODO: desacoplar generateCSVData de getTripBill usando etiquetas en lugar de el orden para identificar secciones
 */

// what types of agenda item are considered for this document (basically, no notes)
const relevantItemTypes = new Set([ "activity", "city", "hotel", "expense", "day" ])

function generateCSVDataRaw(
  trip, route, bill, getCityTranslation, getActivity, name, includeSpanish, internationalTripIncluded
) {
  const { agenda } = route

  let rows = []

  const tp = calculateTripPriceFromBill(trip, bill)

  const total = tp.finalE
  const totalEur = printMoney(total, !"cents")
  const totalYuan = printMoney(tp.finalY, !"cents")
  const adults = countAdults(trip)
  const kids = countKids(trip)
  const totalPersons = adults + kids

  const { firstCity, lastCity } = route.agenda.filter(x => x.city).reduce(
    (results, item) => ({
      firstCity: results.firstCity || item.city,
      lastCity: (item.city === "China" ? results.lastCity : item.city)
    }), {}
  )
  const firstCityCh = getCityTranslation(firstCity)
  const lastCityCh = getCityTranslation(lastCity)

  rows.push(["", `${name}线路及报价`, ""])
  rows.push([])
  rows.push(["人数", `${adults}成人${kids ? `${kids}儿童` : ""}`, ""])
  rows.push(["国内出行城市", trip.origin])
  rows.push(["国外到达城市", firstCityCh])
  rows.push(["国外离境城市", lastCityCh])

  // rows.push([`到达马德里`,   sd.format("DD/MM/YYYY")])
  // rows.push([`巴塞罗那回国`, ed.format("DD/MM/YYYY")])

  rows.push(["日期", "行程项目", "备注", "餐饮"])

  // rows.push([, "西班牙申根旅游签证办理费用", trip.notes]) // visado n shit, notes

  // iterate through the route
  let dayCount = 0
  const lastDayIndex = route.agenda.reduce((accum, item) => (item.type == "day" ? accum + 1 : accum), 0)
  const date = moment(trip.startDate).subtract(1, "day")

  // register cities for coloring
  const cityIndexes = []
  let _lastAdded = null

  const addCityIndex = (city, i) => {
    if (city && city === _lastAdded) {
      last(cityIndexes).push(i)
    }
    else {
      _lastAdded = city
      cityIndexes.push([i])
    }
  }

  const getCitiesTillNextDay = idx => {
    const dayEnd = findIndex(route.agenda.slice(idx + 1), x => x.type == "day")
    const day = route.agenda.slice(idx, idx + dayEnd)
    return uniq(compact(day.map(
      item => item.city || (item.type == "activity" && getActivity(item.id) && getActivity(item.id).city) || null
    )))
      .map(getCityTranslation).join(",")
  }

  const renderItem = (item, idx) => {
    if (item.type === "day") {
      ++dayCount
      date.add(1, "day")

      const todaysCities = getCitiesTillNextDay(idx)
      const mainCity = last(todaysCities)
      addCityIndex(mainCity, idx)
      const r = [
        `第${dayCount}天`,
        `${date.format("YYYY/MM/DD")} ${todaysCities}`,
        null,
        (
          (dayCount == 1 && "自理／自理")
          || (dayCount == lastDayIndex && "早餐／自理")
          || "早餐／自理／自理"
        )
      ]

      return r
    }

    if (item.type === "city") {
      const origenIdx = findLastIndex(agenda, x => x.to || x.city, idx - 1)
      const origen = origenIdx < 0 ? "China" : (agenda[origenIdx].from || agenda[origenIdx].city)

      const basePrice = calculateItemPrice(item.transport.price, adults, kids)
      const fee = getItemFee(item, getFactorGestion(trip))
      const price = fee * basePrice

      const includes = !price ? (
        item.transport.means == "airplane" ? "未包含往返国际机票" : ""
      ) : (
        {
          airplane: `已含往返国际机票 今日参考价${price}欧`,
          train: `已含火车票`,
          bus: `已含大巴票`
        }[item.transport.means] || ""
      )

      return [item.time || "",
        `从${getCityTranslation(origen)}`
        + (transportationIncludesTake(item.transport.means) ? `搭乘` : "")
        + `${getTransportationLabel(item.transport.means, { forceLanguage: "ch" })}`
        + `至${getCityTranslation(item.transport.to)}`,
        includes,
        "",
        includeSpanish ? `${getText("Viaje a")} ${item.transport.to} en ${item.transport.means}` : ""
      ]
    }

    if (item.type === "activity") {
      const a = getActivity(item.id)
      return [item.time || "", a.title, a.clientNote, "", includeSpanish ? a.name : ""]
    }

    if (item.type === "hotel" || item.type === "expense") {
      const expense = (
        item.expense
          ? item.expense
          : (() => { const head = agenda.find(x => x.key == item.ref); return head && head.expense; })()
      )

      return [ null, expense.title]
    }

    return null // no notes!
  }


  rows = rows.concat(
    route.agenda
      .filter(item => relevantItemTypes.has(item.type))
      .map(renderItem)
      .filter(x => !!x)
  )

  rows.push([])

  // Gastos generales
  if (route.expenses.length > 0) {
    rows = rows.concat(compact([
      ["", "其它费用:"],
      ...[internationalTripIncluded ? ["", "国际往返机票"] : null], // vuelo internacional
      ...(route.expenses.map(expense => ["", expense.title || expense.name])),
      []
    ]))
  }

  // Precio no incluye
  rows = rows.concat(compact([
    ["", "此价格不包含："],
    ...[internationalTripIncluded ? null : ["", "国际往返机票"]], // vuelo internacional
    ["", "表格中提及的根据客人需要自行购买的景点门票"],
    ["", "自理就餐费"],
    ["", ""]
  ]))

  rows.push([])
  rows.push([null, `${totalPersons}人${dayCount}天总报价: ${totalEur}€ / ￥${totalYuan}元`])
  rows.push([null, `汇率变动，报价以欧元为准`])

  if (internationalTripIncluded) {
    rows.push(["", `最终报价请根据机票购买当日价格变动请多退少补`])
  }

  // last line:
  // The final quotation is based on the price increase on the day of ticket purchase.
  rows.push([null, "最终报价根据机票购买当日价格涨幅请多退少补"])

  // pad the end
  rows = rows.concat(Array(30).fill([]))

  // pad the right
  rows = rows.map(row => (row.length < 12 ? row.concat(Array(12 - row.length).fill(null)) : row))

  return { data: rows, cityIndexes }
}

/**
 *
 */

type ContratoProps = {
  onClose: () => void,
  trip: Object,
  route: Object,
  bill: Object,
  Activities: Object,
  getCityTranslation: string => string,
}

const stateToProps = (state, props) => ({
  Activities: state.activities,
  getCityTranslation: id => {
    const city = state.cities[id]
    return city && city.chinese ? city.chinese : `⚠️ No existe: ${id}`
  },
  bill: getBillForProps(state, props),
})

/**
 *
 */

class Contrato extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      nameToUse: false,
      includeSpanish: false,
      internationalTripIncluded: false
    }
  }

  props: ContratoProps

  render() {
    return (
      <Modal show onHide={() => this.props.onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>{getText("Descargar contrato")} (<Glyphicon glyph="star" />)</Modal.Title>
        </Modal.Header>
        <Modal.Body>

          <Panel bsStyle="warning">
            <Panel.Heading>{getText("Aviso")}</Panel.Heading>
            <Panel.Body>
              <ul>
                <li>{getText("contrato_warn_1")}</li>
                <li>{getText("contrato_warn_1.5")}</li>
                <li>{getText("contrato_warn_2")}</li>
                <li>{getText("contrato_warn_3")}</li>
                <li>{getText("contrato_warn_4")}</li>
                <li>{getText("contrato_warn_5")}</li>
              </ul>
            </Panel.Body>
          </Panel>

          <TripNamePicker
            trip={this.props.trip}
            value={this.state.nameToUse}
            onChange={nameToUse => this.setState({ nameToUse })}
            config="contrato"
          />

          <Panel>
            <Panel.Heading>Opciones</Panel.Heading>
            <Panel.Body>

              <Checkbox
                checked={this.state.internationalTripIncluded}
                onChange={evt => this.setState({ internationalTripIncluded: evt.target.checked })}
              >
                <span style={{ fontWeight: "bold" }}>{getText("Viaje internacional incluido ✈︎ ")}</span>
              </Checkbox>

              <Checkbox
                checked={this.state.includeSpanish}
                onChange={evt => this.setState({ includeSpanish: evt.target.checked })}
              >
                <span style={{ fontWeight: "bold" }}>{getText("Incluir textos en español")}</span>
              </Checkbox>

            </Panel.Body>
          </Panel>

          <Button
            bsStyle="primary"
            glyph="download"
            disabled={!this.state.nameToUse}
            onClick={() => {
              const name = this.state.nameToUse == "people.leader"
                ? get(this.props.trip, `people.data.${this.props.trip.people.leader}.name`, "")
                : get(this.props.trip, this.state.nameToUse, "")

              const { data, cityIndexes } = generateCSVDataRaw(
                this.props.trip,
                this.props.route,
                this.props.bill,
                this.props.getCityTranslation,
                id => this.props.Activities[id],
                name,
                this.state.includeSpanish,
                this.state.internationalTripIncluded
              )

              FileSaver.saveAs(
                createExcelBlob(
                  data,
                  contratoExcelStyle(data.length, cityIndexes, this.state.internationalTripIncluded)
                ),
                `Contrato ${name}.xlsx`
              )
            }}
          >
            {getText("Descargar XLSX")}
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button bsStyle="success" onClick={() => this.props.onClose()}>{getText("Cerrar")}</Button>
        </Modal.Footer>
      </Modal>
    )
  }
}

export default connect(stateToProps)(Contrato)
